import { t } from "i18next";
import { useState } from "react";

/**
 * Utility function để copy text vào clipboard với fallback support
 * @param text - Text cần copy
 * @returns Promise<boolean> - true nếu copy thành công, false nếu thất bại
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    // Kiểm tra xem clipboard API có khả dụng không
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback method cho các thiết bị không hỗ trợ clipboard API
      return fallbackCopyTextToClipboard(text);
    }
  } catch (err) {
    console.error("Clipboard API failed, trying fallback: ", err);
    return fallbackCopyTextToClipboard(text);
  }
};

/**
 * Fallback method sử dụng document.execCommand
 * @param text - Text cần copy
 * @returns boolean - true nếu copy thành công, false nếu thất bại
 */
const fallbackCopyTextToClipboard = (text: string): boolean => {
  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Đảm bảo element không hiển thị
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    textArea.style.opacity = "0";
    textArea.style.pointerEvents = "none";
    textArea.style.zIndex = "-1";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand("copy");
    document.body.removeChild(textArea);

    return successful;
  } catch (err) {
    console.error("Fallback copy method failed: ", err);
    return false;
  }
};

/**
 * Hook để sử dụng copy clipboard với state management
 * @returns Object với copyText function và isCopied state
 */
export const useCopyToClipboard = () => {
  const [isCopied, setIsCopied] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const copyText = async (text: string, resetDelay: number = 2000) => {
    if (isProcessing) return false;

    setIsProcessing(true);

    try {
      const success = await copyToClipboard(text);

      if (success) {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, resetDelay);
      } else {
        alert(t("Unable to copy link. Please try again or copy manually."));
      }

      return success;
    } finally {
      setTimeout(() => {
        setIsProcessing(false);
      }, 500);
    }
  };

  return { copyText, isCopied, isProcessing };
};
